import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowPathIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import SummaryTab from "../../components/thirteenWeekCashFlow/SummaryTab";
import DailyForecastTab from "../../components/thirteenWeekCashFlow/DailyForecastTab";
import CashActivityReviewTab from "../../components/thirteenWeekCashFlow/CashActivityReviewTab";
import RevenuePlanTab from "../../components/thirteenWeekCashFlow/RevenuePlanTab";
import DisbursementCalendarTab from "../../components/thirteenWeekCashFlow/DisbursementCalendarTab";
import ConfigurationTab from "../../components/thirteenWeekCashFlow/ConfigurationTab";
import PublishedForecastsTab from "../../components/thirteenWeekCashFlow/PublishedForecastsTab";
import {
  getThirteenWeekDisbursements,
  getThirteenWeekDisbursementHistory,
  getThirteenWeekCards,
  getThirteenWeekHolidays,
  getThirteenWeekRevenuePlan,
  getThirteenWeekRules,
  getThirteenWeekSchedules,
  getThirteenWeekSettings,
  getThirteenWeekSummary,
  getThirteenWeekHistory,
  getThirteenWeekTransactions,
  importThirteenWeekData,
} from "../../slices/finance/thirteenWeekCashFlowSlice";
import { dateLabel } from "../../components/thirteenWeekCashFlow/money";
import { thirteenWeekCashFlowApi } from "../../helpers/thirteenWeekCashFlowApi";

const tabs = [
  ["summary", "Summary", "Weekly liquidity and source-traceable cash flow."],
  [
    "daily",
    "Daily Cash Forecast",
    "91-day opening cash, inflows, disbursements, and ending cash.",
  ],
  [
    "cashReview",
    "Cash Activity Review",
    "Review imported cash activity and categorization.",
  ],
  [
    "revenue",
    "Revenue Plan",
    "CEO forecast targets and collection allocation.",
  ],
  [
    "disbursements",
    "Disbursement Calendar",
    "Unified scheduled cash out by source.",
  ],
  [
    "published",
    "Published Forecasts",
    "Each Monday's saved forecast, with version-to-version changes.",
  ],
  ["config", "Configuration", "Liquidity thresholds and source coverage."],
];

export default function ThirteenWeekCashFlow() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useSelector((root) => root.thirteenWeekCashFlow || {});
  const historyRequested = useRef(false);
  const disbursementHistoryRequested = useRef(false);
  const requested =
    searchParams.get("tab") || searchParams.get("view") || "summary";
  const active = tabs.some(([key]) => key === requested)
    ? requested
    : "summary";
  const [week, setWeek] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const tab = tabs.find(([key]) => key === active) || tabs[0];
  const requestHistory = useCallback(() => {
    if (historyRequested.current || state.history) return;
    historyRequested.current = true;
    dispatch(getThirteenWeekHistory());
  }, [dispatch, state.history]);
  const requestDisbursementHistory = useCallback(() => {
    if (disbursementHistoryRequested.current || state.disbursementHistory) return;
    disbursementHistoryRequested.current = true;
    dispatch(getThirteenWeekDisbursementHistory());
  }, [dispatch, state.disbursementHistory]);

  const setTab = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    next.delete("view");
    setSearchParams(next, { replace: true });
  };

  const refreshSummary = useCallback(async () => {
    setRefreshing(true);
    try {
      const requests = [dispatch(getThirteenWeekSummary({ week })).unwrap()];
      if (state.revenuePlan) {
        requests.push(dispatch(getThirteenWeekRevenuePlan()).unwrap());
      }
      await Promise.all(requests);
    } catch (error) {
      toast.error(error || "Summary could not be refreshed", {
        position: "bottom-right",
      });
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, state.revenuePlan, week]);

  const refreshReview = useCallback(
    (filters = {}) => {
      dispatch(getThirteenWeekTransactions(filters));
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(getThirteenWeekSummary({ week }));
    dispatch(getThirteenWeekSettings());
    dispatch(getThirteenWeekCards());
    dispatch(getThirteenWeekSchedules());
    dispatch(getThirteenWeekRules());
    dispatch(getThirteenWeekHolidays());
  }, [dispatch, week]);

  useEffect(() => {
    if (active === "summary" || active === "daily") requestHistory();
    if (active === "cashReview") refreshReview();
    if (active === "revenue") dispatch(getThirteenWeekRevenuePlan());
    if (active === "disbursements")
      dispatch(getThirteenWeekDisbursements({ all: 1 }));
  }, [active, dispatch, refreshReview, requestHistory, week]);

  const exportCsv = () => {
    const weekly = state.summary?.weekly || [];
    const headers = [
      "Week",
      "Start",
      "End",
      "Beginning cash",
      "Cash in",
      "Cash out",
      "Net cash flow",
      "Ending cash",
    ];
    const rows = weekly.map((row) => [
      row.weekIndex + 1,
      row.start,
      row.end,
      row.beginningCash,
      row.totalCashIn,
      row.totalCashOut,
      row.netCashFlow,
      row.endingCash,
    ]);
    const csv = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = "thirteen-week-cash-flow.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const toggleSetting = useCallback(
    async (key, value) => {
      try {
        await thirteenWeekCashFlowApi.updateSetting(key, value);
        toast.success("Configuration saved", { position: "bottom-right" });
        dispatch(getThirteenWeekSettings());
        await refreshSummary();
      } catch (error) {
        toast.error(
          error?.response?.data?.error || "Configuration could not be saved",
          { position: "bottom-right" },
        );
      }
    },
    [dispatch, refreshSummary],
  );

  const content = useMemo(() => {
    if (active === "summary")
      return (
        <SummaryTab
          summary={state.summary}
          settings={state.settings}
          loading={state.loading?.summary}
          error={state.errors?.summary}
          history={state.history}
          historyLoading={state.loading?.history}
          historyError={state.errors?.history}
          selectedWeek={week}
          onSelectWeek={setWeek}
          onToggleSetting={toggleSetting}
        />
      );
    if (active === "daily")
      return (
        <DailyForecastTab
          summary={state.summary}
          holidays={state.holidays}
          loading={state.loading?.summary}
          error={state.errors?.summary}
          dispatch={dispatch}
          settings={state.settings}
          onRefresh={refreshSummary}
          history={state.history}
          historyLoading={state.loading?.history}
          historyError={state.errors?.history}
          onHistoryRequested={requestHistory}
        />
      );
    if (active === "cashReview")
      return (
        <CashActivityReviewTab
          data={state.transactions}
          summary={state.summary}
          loading={state.loading?.transactions}
          error={state.errors?.transactions}
          onRefresh={refreshReview}
        />
      );
    if (active === "revenue")
      return (
        <RevenuePlanTab
          data={state.revenuePlan}
          loading={state.loading?.revenuePlan}
          error={state.errors?.revenuePlan}
          onRefresh={() => dispatch(getThirteenWeekRevenuePlan())}
        />
      );
    if (active === "disbursements")
      return (
        <DisbursementCalendarTab
          data={state.disbursements}
          holidays={state.holidays}
          loading={state.loading?.disbursements}
          error={state.errors?.disbursements}
          week={week}
          onWeekChange={setWeek}
          settings={state.settings}
          history={state.disbursementHistory}
          historyLoading={state.loading?.disbursementHistory}
          historyError={state.errors?.disbursementHistory}
          onHistoryRequested={requestDisbursementHistory}
        />
      );
    if (active === "published") return <PublishedForecastsTab />;
    return (
      <ConfigurationTab
        settings={state.settings}
        schedules={state.schedules}
        rules={state.rules}
        builtinMappings={state.builtinMappings}
        holidays={state.holidays}
        cards={state.cards}
        summary={state.summary}
        loading={state.loading?.settings}
        error={state.errors?.settings}
        onImport={(payload) =>
          dispatch(importThirteenWeekData(payload)).unwrap()
        }
        onRefresh={() => {
          dispatch(getThirteenWeekSettings());
          dispatch(getThirteenWeekSchedules());
          dispatch(getThirteenWeekRules());
          dispatch(getThirteenWeekHolidays());
          dispatch(getThirteenWeekCards());
          dispatch(getThirteenWeekSummary({ week }));
        }}
      />
    );
  }, [
    active,
    dispatch,
    requestDisbursementHistory,
    requestHistory,
    refreshReview,
    refreshSummary,
    state,
    toggleSetting,
    week,
  ]);

  return (
    <main className="h-full w-full bg-white p-4 sm:p-8">
      <div className="flex flex-col gap-4">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <h1 className="whitespace-nowrap text-[14px] font-bold leading-none tracking-tight text-gray-900 sm:text-[20px]">
                13-Week Cash Flow
              </h1>
              {state.summary?.window?.start && (
                <p className="hidden text-[13px] tabular-nums text-gray-400 sm:inline">
                  Week of {dateLabel(state.summary.window.start)} · Kings Loot ·{" "}
                  {state.summary.window.timezone}
                </p>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">{tab[2]}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={refreshSummary}
              disabled={refreshing}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowPathIcon
                className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
            <button
              type="button"
              onClick={exportCsv}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-700 hover:bg-gray-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4 text-gray-400" />
              Export CSV
            </button>
          </div>
        </header>
        <nav className="flex flex-wrap gap-1 border-b border-gray-200 pb-2">
          {tabs.map(([key, label]) => (
            <button
              type="button"
              key={key}
              onClick={() => setTab(key)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${active === key ? "bg-black text-white" : "text-gray-600 hover:bg-gray-100"}`}
            >
              {label}
            </button>
          ))}
        </nav>
        {content}
        {state.summary?.openingCash?.asOf && (
          <p className="text-right text-[11px] text-gray-400">
            Cash imported{" "}
            {dateLabel(state.summary.openingCash.asOf)}{" "}
            · amounts shown without cents
          </p>
        )}
      </div>
    </main>
  );
}
